import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBudgetEntities } from '../hooks/useBudgetEntities';
import {
  Category,
  CategoryItem,
  Destination,
  DestinationItem,
  Person,
  ResponsibleItem,
  Transaction,
} from '../types/budget';
import { FormInput } from './FormInput';
import { FormSelectCreatable } from './FormSelectCreatable';

interface TransactionFormProps {
  transaction?: Transaction;
  isInverseTransaction?: boolean;
  onSubmit: (transactions: Array<Omit<Transaction, 'id'>>) => void;
  onCancel: () => void;
  budgetId: number | null;
  categories: CategoryItem[];
  responsibles: ResponsibleItem[];
  destinations: DestinationItem[];
  transactionType?: 'income' | 'expense';
}

export const TransactionForm: React.FC<TransactionFormProps> = ({
  transaction,
  isInverseTransaction = false,
  onSubmit,
  onCancel,
  budgetId,
  categories,
  responsibles,
  destinations,
  transactionType,
}) => {
  const { t } = useTranslation();
  const { addCategory, addResponsible, addDestination } = useBudgetEntities(budgetId);

  const [formData, setFormData] = useState<Omit<Transaction, 'id'>>({
    category: transaction?.category ?? categories[0]?.id ?? '',
    responsible: transaction?.responsible ?? responsibles[0]?.id ?? '',
    description: transaction?.description ?? '',
    destination: transaction?.destination ?? destinations[0]?.id ?? '',
    amount: transaction?.amount ?? 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // If it's an expense, make the amount negative
    const finalAmount =
      transactionType === 'expense' ? -Math.abs(formData.amount) : Math.abs(formData.amount);
    onSubmit([{ ...formData, amount: finalAmount }]);
  };

  // Handle creating a new category
  const handleCreateCategory = (categoryName: string) => {
    if (addCategory) {
      addCategory(categoryName);
    }
  };

  // Handle creating a new responsible
  const handleCreateResponsible = (responsibleName: string) => {
    if (addResponsible) {
      addResponsible(responsibleName);
    }
  };

  // Handle creating a new destination
  const handleCreateDestination = (destinationName: string) => {
    if (addDestination) {
      addDestination(destinationName);
    }
  };

  // Determine the button text based on the transaction type
  const getButtonText = () => {
    if (isInverseTransaction) {
      return t('common.add');
    }
    if (transaction) {
      return t('common.save');
    }
    return transactionType === 'expense' ? t('transaction.newExpense') : t('transaction.newIncome');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <FormSelectCreatable
          label={t('transaction.category')}
          value={formData.category}
          onChange={(value) => setFormData({ ...formData, category: value as Category })}
          options={categories}
          onCreateOption={handleCreateCategory}
        />

        <FormSelectCreatable
          label={t('transaction.responsible')}
          value={formData.responsible}
          onChange={(value) => setFormData({ ...formData, responsible: value as Person })}
          options={responsibles}
          onCreateOption={handleCreateResponsible}
        />

        <FormInput
          label={t('transaction.description')}
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value as string })}
          required
          placeholder={t('transaction.description')}
        />

        <FormSelectCreatable
          label={t(
            transactionType === 'income'
              ? 'transaction.destinationIncome'
              : 'transaction.destinationExpense'
          )}
          labelElement={
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                {t(
                  transactionType === 'income'
                    ? 'transaction.destinationIncome'
                    : 'transaction.destinationExpense'
                )}
              </label>
              <div className="group relative">
                <div className="flex items-center justify-center rounded-full p-1 text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400">
                  <InformationCircleIcon className="h-4 w-4" />
                </div>
                <div className="invisible absolute right-0 top-full z-10 mt-2 w-72 rounded-lg bg-white p-2 text-sm text-slate-500 shadow-lg transition-all group-hover:visible dark:bg-surface-800 dark:text-slate-300">
                  {t(
                    transactionType === 'income'
                      ? 'transaction.destinationIncomeHelp'
                      : 'transaction.destinationExpenseHelp'
                  )}
                </div>
              </div>
            </div>
          }
          value={formData.destination}
          onChange={(value) => setFormData({ ...formData, destination: value as Destination })}
          options={destinations}
          onCreateOption={handleCreateDestination}
        />

        <FormInput
          label={t('transaction.amount')}
          value={Math.abs(formData.amount)}
          onChange={(value) => setFormData({ ...formData, amount: value as number })}
          type="number"
          step="0.01"
          placeholder="0.00"
        />
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-500 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-500 dark:bg-surface-800 dark:text-slate-300 dark:hover:bg-surface-700"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          data-attr="transaction-form-submit-button"
          className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
        >
          {getButtonText()}
        </button>
      </div>
    </form>
  );
};
